import { get } from 'lodash'
import { isAfter } from 'date-fns'

import { store } from 'store/reducers'
import { Parse } from 'services/Dates'

type AttendancePermission = 'attendanceSee' | 'attendanceSeeOthers' | 'attendanceCancel' | 'attendanceCreate' | 'attendanceModify' | 'attendanceComments'
type AvailabilityPermission = 'availabilitySee' | 'availabilityCreate' | 'availabilityModify'
type ManagerAvailibilityPermission =
  | 'manageAvailabilitySee'
  | 'managerAvailabilitySee'
  | 'manageAvailabilityEdit'
  | 'manageAvailabilityRequestChanges'
  | 'manageAvailabilityApprove'
type TimeoffPermission = 'timeoffSee' | 'timeoffCreate' | 'timeoffModify' | 'timeoffCancel' | 'timeoffComments'
type SchedulePermission =
  | 'scheduleSee'
  | 'scheduleSeeOthers'
  | 'scheduleSeeCoworkers'
  | 'scheduleCreate'
  | 'scheduleModify'
  | 'scheduleDelete'
  | 'scheduleAssignEmployee'
  | 'scheduleComments'
  | 'scheduleCancel'
  | 'schedulePublish'
  | 'scheduleListCreation'
  | 'scheduleTemplateSee'
  | 'scheduleTemplateCreate'
  | 'scheduleTemplateApply'
  | 'scheduleCapacity'
  | 'scheduleConfirm'
  | 'scheduleHistorySee'
  | 'scheduleOnlyAssignEmployee'
type TemplateTypePermission = 'templateSee' | 'userTemplateSee'
type BulletinPermission = 'bulletinSee' | 'bulletinSeeReadBy' | 'bulletinManage' | 'bulletinNotification'
type ContactListPermission =
  | 'contactListSee'
  | 'contactListControl'
  | 'contactListDelete'
  | 'contactListArchive'
  | 'contactListApprove'
  | 'contactBoost'
  | 'contactNotifications'
  | 'contactMessage'
type PunchPermission = 'punchOnShifts' | 'seeOwnPunches' | 'punchTerminalLaunch'
type AdminstrationPermission =
  | 'settingApplication'
  | 'attributeSee'
  | 'attributeManage'
  | 'attributeAssign'
  | 'breakManage'
  | 'departmentManage'
  | 'hiringDataSee'
  | 'hiringDataManage'
  | 'wagePremiumManage'
  | 'wagePremiumOverride'
  | 'permissionManage'
  | 'apiToken'
  | 'positionManage'
  | 'positionDelete'
  | 'punchRuleManage'
  | 'requestOptionManage'
  | 'scheduleTypeManage'
  | 'structureManage'
  | 'tagManage'
  | 'teamManage'
  | 'configManage'
  | 'holidayManage'

type ManagerPermission =
  | 'auditLogSee'
  | 'manageAttendance'
  | 'manageAttendanceAcknowledge'
  | 'manageAttendanceJustify'
  | 'manageAttendanceUnapprouve'
  | 'manageAttendanceCancel'
  | 'manageAttendanceDelete'
  | 'manageAttendanceEdit'
  | 'manageTimeOff'
  | 'manageTimeOffCancel'
  | 'manageTimeOffDelete'
  | 'manageTimeOffEdit'
  | 'manageUser'
  | 'manageAvailability'

type UserSeePermission =
  | 'userSee'
  | 'userSeePersonalInfo'
  | 'userSeeHiringInfo'
  | 'userSeeContactInfo'
  | 'userSeeConnectionInfo'
  | 'userSeePayInfo'
  | 'userCreate'
  | 'userUpdate'
  | 'userUpdatePersonnalInfo'
  | 'userUpdateHiringInfo'
  | 'userUpdateContactInfo'
  | 'userUpdateConnectionInfo'
  | 'userArchive'
  | 'userDelete'
  | 'userOnboarding'
  | 'userSeeDiff'
  | 'userComments'
  | 'userAttachments'
  | 'userManageSpecificRestrictions'
  | 'permissionsAssignation'
  | 'departmentsAssignation'

type ProjectSeePermission = 'projectSee' | 'projectManage' | 'reportsSee'
type PunchCardSeePermission =
  | 'punchCardSee'
  | 'punchCardManage'
  | 'reportsSee'
  | 'punchCardDelete'
  | 'punchCardChangeLockDate'
  | 'punchCardManageLocked'
  | 'punchCardApproveOwn'
type ImportPermission = 'importUser' | 'importTag' | 'importProject' | 'importSchedule' | 'importTimeOff' | 'importPunch'
type UserProfilePermission = 'manageEmails' | 'managePhoneNumbers' | 'limitToReportsTo' | 'defaultReportsTo'
type PanelPermission = 'managerPanel' | 'adminPanel' | 'isMeringoAdmin' | 'isDev' | 'isStaff'

export type AccessType =
  | AttendancePermission
  | AvailabilityPermission
  | ManagerAvailibilityPermission
  | TimeoffPermission
  | SchedulePermission
  | TemplateTypePermission
  | BulletinPermission
  | ContactListPermission
  | PunchPermission
  | AdminstrationPermission
  | ManagerPermission
  | UserSeePermission
  | ProjectSeePermission
  | PunchCardSeePermission
  | ImportPermission
  | UserProfilePermission
  | PanelPermission

const hasAccessToThis = (type: AccessType): string | boolean => {
  const {
    auth,
    resources: { hiringDataById, permissionsById },
    users: { userInfo }
  } = store.getState() as MStore.RootState

  const { permissions, groups, staff, dev } = auth.decodedToken ?? {}
  switch (type) {
    // ---- Attendance permissions ----
    case 'attendanceSee':
      return get(permissions, `leave.see`, false)
    case 'attendanceSeeOthers':
      return get(permissions, `leave.seeOthers`, false)
    case 'attendanceCancel':
      return get(permissions, `leave.cancel`, false)
    case 'attendanceCreate':
      return get(permissions, `leave.create`, false)
    case 'attendanceModify':
      return get(permissions, `leave.update`, false)
    case 'attendanceComments':
      return get(permissions, `leave.comments`, false)

    // ---- Availability permissions ----
    case 'availabilitySee':
      return get(permissions, `availabilities.see`, false)
    case 'availabilityCreate':
    case 'availabilityModify': {
      // if the contact is not allowed and the contract is not expired
      const canUpdateAvailabilities = userInfo?.hiringData?.reduce((acc, data) => {
        // Check if that specific contract prevents the change and is not expired
        const contractExpired = !!data.end && isAfter(new Date(), Parse(data.end, { inFormat: 'date' }))
        const contractAllowsChange = !hiringDataById[data.hiringDataId].cannotUpdateAvailabilities || contractExpired
        return acc ? contractAllowsChange : false
      }, true)

      // Set if the user can update / create availabilities
      return canUpdateAvailabilities ? get(permissions, `availabilities.edit`, false) : false
    }

    // ---- Manager  Availability permissions ----
    case 'manageAvailabilitySee':
      return get(permissions, `availabilities.seeOthers`, false)
    case 'manageAvailabilityEdit':
      return get(permissions, `availabilities.editOthers`, false)
    case 'manageAvailabilityRequestChanges':
      return get(permissions, `availabilities.requestChanges`, false)
    case 'manageAvailabilityApprove':
      return get(permissions, `availabilities.approve`, false)

    // ---- Time off permissions ----
    case 'timeoffSee':
      return get(permissions, `vacation.see`, false)
    case 'timeoffCreate':
      return get(permissions, `vacation.create`, false)
    case 'timeoffModify':
      return get(permissions, `vacation.update`, false)
    case 'timeoffCancel':
      return get(permissions, `vacation.cancel`, false)
    case 'timeoffComments':
      return get(permissions, `vacation.comments`, false)

    // ---- Schedule permissions ----
    case 'scheduleSee':
      return get(permissions, `schedule.see`, false)
    case 'scheduleSeeOthers':
      return get(permissions, `schedule.seeOthers`, false)
    case 'scheduleSeeCoworkers':
      return get(permissions, `schedule.seeOthers`, false) || get(permissions, 'schedule.scheduleSeeCoworkers', false)
    case 'scheduleCreate':
      return get(permissions, `schedule.create`, false)
    case 'scheduleModify':
      return get(permissions, `schedule.update`, false)
    case 'scheduleDelete':
      return get(permissions, `schedule.delete`, false)
    case 'scheduleCancel':
      return get(permissions, `schedule.cancel`, false)
    case 'schedulePublish':
      return get(permissions, `schedule.publish`, false)
    case 'scheduleAssignEmployee':
      return get(permissions, `schedule.assignEmployee`, false)
    case 'scheduleListCreation':
      return get(permissions, `schedule.listCreation`, false)
    case 'scheduleTemplateSee':
      return get(permissions, `schedule.templateCreation`, false) || get(permissions, `schedule.templateApplication`, false)
    case 'scheduleTemplateCreate':
      return get(permissions, `schedule.templateCreation`, false)
    case 'scheduleTemplateApply':
      return get(permissions, `schedule.templateApplication`, false)
    case 'scheduleCapacity':
      return get(permissions, `schedule.seeCapacity`, false)
    case 'scheduleConfirm':
      return get(permissions, `schedule.confirm`, false)
    case 'scheduleHistorySee':
      return get(permissions, 'schedule.seeHistory', false)
    case 'scheduleOnlyAssignEmployee':
      return get(permissions, 'schedule.assignEmployee', false) && !get(permissions, 'schedule.update', false)

    // ---- template permissions ----
    case 'templateSee':
      return get(permissions, `schedule.templateCreation`, false) || get(permissions, `schedule.templateApplication`, false)
    case 'userTemplateSee':
      return get(permissions, `user.create`, false)

    // ---- Bulletin permissions ----
    case 'bulletinSee':
      return get(permissions, `bulletin.see`, false)
    case 'bulletinSeeReadBy':
      return get(permissions, `bulletin.seeReadBy`, false)
    case 'bulletinManage':
      return get(permissions, `bulletin.manage`, false)
    case 'bulletinNotification':
      return get(permissions, `bulletin.sendNotifications`, false)

    // ---- Contact list permissions ----
    case 'contactListSee':
      return get(permissions, `contactLists.see`, false)
    case 'contactListControl':
      return get(permissions, `contactLists.control`, false)
    case 'contactListDelete':
      return get(permissions, `contactLists.delete`, false)
    case 'contactListArchive':
      return get(permissions, `contactLists.archive`, false)
    case 'contactListApprove':
      return get(permissions, `contactLists.approuve`, false)
    case 'contactBoost':
      return get(permissions, `contact.boostList`, false)
    case 'contactNotifications':
      return get(permissions, `contact.notification`, false)
    case 'contactMessage': {
      return get(permissions, `contactLists.viewMessagesInApp`, false)
    }

    // ---- Punch permissions ----
    case 'punchOnShifts': {
      return get(permissions, `punchTerminal.punchOnShifts`, false)
    }
    case 'seeOwnPunches': {
      return get(permissions, `punchTerminal.seeOwnPunches`, false) || get(permissions, `punchTerminal.punchOnShifts`, false)
    }
    case 'punchTerminalLaunch':
      return get(permissions, `punchTerminal.startSession`, false)

    /**
     * !!!!!!!!!!!!!!! ADMINISTRATION PERMISSIONS !!!!!!!!!!!!!!!
     */
    case 'settingApplication':
      return get(permissions, `setting.application`, false)
    case 'attributeSee':
      return get(permissions, `attribute.see`, false)
    case 'attributeManage':
      return get(permissions, `attribute.create`, false)
    case 'attributeAssign':
      return get(permissions, `attribute.assign`, false)
    case 'breakManage':
      return get(permissions, `breaks.create`, false)
    case 'departmentManage':
      return get(permissions, `departments.create`, false)
    case 'hiringDataSee':
      return get(permissions, `hiringData.see`, false)
    case 'hiringDataManage':
      return get(permissions, `hiringData.create`, false)
    case 'wagePremiumManage':
      return get(permissions, `pay.manageWagePremiums`, false)
    case 'wagePremiumOverride':
      return get(permissions, `pay.overrideWagePremiums`, false)
    case 'permissionManage':
      return get(permissions, `permissions.manage`, false)
    case 'apiToken':
      return get(permissions, `permissions.apiToken`, false)
    case 'positionManage':
      return get(permissions, `structure.create`, false)
    case 'positionDelete':
      return get(permissions, `structure.delete`, false)
    case 'punchRuleManage':
      return get(permissions, `punchRules.manage`, false)
    case 'requestOptionManage':
      return get(permissions, `requestsFields.manage`, false)
    case 'scheduleTypeManage':
      return get(permissions, `scheduleTypes.manage`, false)
    case 'structureManage':
      return get(permissions, `structure.order`, false)
    case 'tagManage':
      return get(permissions, `tags.create`, false)
    case 'teamManage':
      return get(permissions, `teams.modify`, false)
    case 'configManage':
      return get(permissions, `listConfigs.manage`, false)
    case 'holidayManage':
      return get(permissions, 'holidays.manage', false)

    // ---- MANAGER PERMISSIONS ----
    case 'auditLogSee':
      return get(permissions, `auditLog.see`, false)
    case 'manageAttendance':
      return get(permissions, `leave.seeOthers`, false)
    case 'manageAttendanceAcknowledge':
      return get(permissions, `leave.acknowledge`, false)
    case 'manageAttendanceJustify':
      return get(permissions, `leave.justify`, false)
    case 'manageAttendanceUnapprouve':
      return get(permissions, `leave.unapprouve`, false)
    case 'manageAttendanceCancel':
      return get(permissions, `leave.cancel`, false)
    case 'manageAttendanceDelete':
      return get(permissions, `leave.delete`, false)
    case 'manageAttendanceEdit':
      return get(permissions, `leave.update`, false) || get(permissions, `leave.createOthers`, false)

    case 'manageTimeOff':
      return get(permissions, `vacation.seeOthers`, false)
    case 'manageTimeOffCancel':
      return get(permissions, `vacation.cancel`, false)
    case 'manageTimeOffDelete':
      return get(permissions, `vacation.delete`, false)
    case 'manageTimeOffEdit':
      return get(permissions, `vacation.update`, false) || get(permissions, `vacation.createOthers`, false)

    case 'manageUser':
      return hasAccessToThis('userCreate') || hasAccessToThis('userUpdate')

    case 'manageAvailability':
      return get(permissions, `availabilities.seeOthers`, false)

    case 'userSee':
      return get(permissions, `user.see`, false)
    case 'userSeePersonalInfo':
      return get(permissions, `user.seePersonalInfo`, false)
    case 'userSeeHiringInfo':
      return get(permissions, `user.seeHiringInfo`, false)
    case 'userSeeContactInfo':
      return get(permissions, `user.seeContactInfo`, false)
    case 'userSeeConnectionInfo':
      return get(permissions, `user.seeConnectionInfo`, false)
    case 'userSeePayInfo':
      return get(permissions, `user.seePayInfo`, false)

    case 'userCreate':
      return get(permissions, `user.create`, false)
    case 'userUpdate':
      return (
        hasAccessToThis('userUpdatePersonnalInfo') ||
        hasAccessToThis('userUpdateHiringInfo') ||
        hasAccessToThis('userUpdateContactInfo') ||
        hasAccessToThis('userUpdateConnectionInfo')
      )
    case 'userUpdatePersonnalInfo':
      return get(permissions, `user.updatePersonnalInfo`, false)
    case 'userUpdateHiringInfo':
      return get(permissions, `user.updateHiringInfo`, false)
    case 'userUpdateContactInfo':
      return get(permissions, `user.updateContactInfo`, false)
    case 'userUpdateConnectionInfo':
      return get(permissions, `user.updateConnectionInfo`, false)
    case 'userArchive':
      return get(permissions, `user.archive`, false)
    case 'userDelete':
      return get(permissions, `user.delete`, false)
    case 'userOnboarding':
      return get(permissions, `user.onboarding`, false)
    case 'userSeeDiff':
      return get(permissions, `user.seeDiff`, false)
    case 'userComments':
      return get(permissions, `user.comments`, false)
    case 'userAttachments':
      return get(permissions, `user.attachments`, false)
    case 'userManageSpecificRestrictions':
      return get(permissions, `user.manageSpecificRestrictions`, false)
    case 'permissionsAssignation':
      return get(permissions, `permissions.assign`, false)
    case 'departmentsAssignation':
      return get(permissions, `departments.assign`, false)

    case 'projectSee':
      return get(permissions, `projects.see`, false)
    case 'projectManage':
      return get(permissions, `projects.create`, false) || get(permissions, `projects.update`, false) || get(permissions, `projects.delete`, false)
    case 'reportsSee':
      return get(permissions, `reports.see`, false)

    case 'punchCardSee':
      return get(permissions, `punchCards.see`, false)
    case 'punchCardManage':
      return get(permissions, `punchCards.modify`, false)
    case 'punchCardDelete':
      return get(permissions, `punchCards.delete`, false)
    case 'punchCardChangeLockDate':
      return get(permissions, `punchCards.changeLockDate`, false)
    case 'punchCardManageLocked':
      return get(permissions, `punchCards.modifyLocked`, false)
    case 'punchCardApproveOwn':
      return get(permissions, `punchCards.enableApproveOwn`, false)
    case 'importUser':
      return get(permissions, `import.user`, false)
    case 'importTag':
      return get(permissions, `import.tag`, false)
    case 'importProject':
      return get(permissions, `import.project`, false)
    case 'importSchedule':
      return get(permissions, `import.schedule`, false)
    case 'importTimeOff':
      return get(permissions, `import.vacation`, false)
    case 'importPunch':
      return get(permissions, `import.punch`, false)

    // User profile permissions
    case 'manageEmails':
      return get(permissions, 'profile.updateEmails', false)
    case 'managePhoneNumbers':
      return get(permissions, 'profile.updatePhoneNumbers', false)

    // Limit user to only reportsTo filter
    case 'limitToReportsTo':
      return !!groups && groups.some(groupId => permissionsById[groupId]?.limitToReportsTo)
    case 'defaultReportsTo': {
      const allDefaults = groups?.map(groupId => permissionsById[groupId]?.defaultReportsTo || 'none') || []
      if (allDefaults.includes('team') && allDefaults.includes('project')) return 'none'
      if (allDefaults.includes('team')) return 'team'
      if (allDefaults.includes('project')) return 'project'
      return 'none'
    }

    // ---- Has access to one or more of the manager permissions ----
    case 'managerPanel':
      return isAManager()
    case 'adminPanel':
      return isAnAdmin()
    case 'isMeringoAdmin':
      return !!staff || !!dev
    case 'isDev':
      return !!dev
    case 'isStaff':
      return !!staff

    default:
      return false
  }

  function isAManager() {
    const hasAccessOfOneOfTheSection =
      hasAccessToThis('manageAttendance') ||
      hasAccessToThis('auditLogSee') ||
      hasAccessToThis('manageAvailability') ||
      hasAccessToThis('bulletinManage') ||
      hasAccessToThis('punchCardSee') ||
      hasAccessToThis('projectManage') ||
      hasAccessToThis('scheduleSeeOthers') ||
      hasAccessToThis('manageTimeOff') ||
      hasAccessToThis('reportsSee') ||
      hasAccessToThis('manageUser')

    // To have access to the manager portal, you must have access to at least one section
    // AND be part of the hardcoded access or have the manager portal enabled on your company
    return hasAccessOfOneOfTheSection
  }

  function isAnAdmin() {
    const hasAccessOfOneOfTheSection =
      hasAccessToThis('attributeManage') ||
      hasAccessToThis('breakManage') ||
      hasAccessToThis('configManage') ||
      hasAccessToThis('departmentManage') ||
      hasAccessToThis('hiringDataManage') ||
      hasAccessToThis('wagePremiumManage') ||
      hasAccessToThis('wagePremiumOverride') ||
      hasAccessToThis('permissionManage') ||
      hasAccessToThis('positionManage') ||
      hasAccessToThis('punchRuleManage') ||
      hasAccessToThis('requestOptionManage') ||
      hasAccessToThis('scheduleTypeManage') ||
      hasAccessToThis('structureManage') ||
      hasAccessToThis('tagManage') ||
      hasAccessToThis('teamManage') ||
      hasAccessToThis('holidayManage')

    // To have access to the admin portal, you must have access to at least one section
    // AND be part of the hardcoded access or have the admin portal enabled on your company
    return hasAccessOfOneOfTheSection
  }
}

export default hasAccessToThis
